import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ContractorInvoiceBillingMode, ContractorProfile, ContractorW9Status } from '../../types';
import {
  getContractorProfile,
  upsertContractorProfile,
  uploadContractorW9,
  getContractorW9DownloadUrl,
} from '../../utils/contractorTimesheetService';
import { isContractorW9OnFile, parseContractorW9Status } from '../../utils/contractorW9';
import { canManageContractorPayables } from '../../utils/contractorPayablesAccess';
import { useAuth } from '../../utils/AuthContext';
import { FileText, Upload } from 'lucide-react';
import { getContractorSignedAgreementDayRate } from '../../utils/contractorOnboardFirestore';

export interface ContractorProfileEditorHandle {
  /** Persists current form when staff can manage payables. Returns false if save failed. */
  saveIfNeeded: () => Promise<boolean>;
}

interface ContractorProfileEditorProps {
  userId: string;
  displayName: string;
}

const W9_LABELS: Record<ContractorW9Status, string> = {
  missing: 'Missing',
  on_file: 'On file',
  expired: 'Expired',
};

const ContractorProfileEditor = forwardRef<ContractorProfileEditorHandle, ContractorProfileEditorProps>(
  function ContractorProfileEditor({ userId, displayName }, ref) {
  const { user } = useAuth();
  const canManage = canManageContractorPayables({
    authRole: user?.role,
    designations: user?.designations,
  });

  const [profile, setProfile] = useState<ContractorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [businessName, setBusinessName] = useState('');
  const [defaultHourlyRate, setDefaultHourlyRate] = useState('');
  const [defaultDayRate, setDefaultDayRate] = useState('');
  const [invoiceBillingMode, setInvoiceBillingMode] = useState<ContractorInvoiceBillingMode>('hourly');
  const [qbVendorName, setQbVendorName] = useState('');
  const [w9Status, setW9Status] = useState<ContractorW9Status>('missing');
  const [dayRateFromAgreement, setDayRateFromAgreement] = useState<number | null>(null);

  const applyAgreementDayRateIfEmpty = useCallback(async (currentDayRateText: string) => {
    const parsed = parseFloat(currentDayRateText);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    const fromAgreement = await getContractorSignedAgreementDayRate(userId);
    if (fromAgreement != null) {
      setDefaultDayRate(String(fromAgreement));
      setDayRateFromAgreement(fromAgreement);
      return fromAgreement;
    }
    return null;
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const p = await getContractorProfile(userId);
        if (cancelled) return;
        setProfile(p);
        if (p) {
          setBusinessName(p.businessName || '');
          setDefaultHourlyRate(p.defaultHourlyRate > 0 ? String(p.defaultHourlyRate) : '');
          setDefaultDayRate(p.defaultDayRate && p.defaultDayRate > 0 ? String(p.defaultDayRate) : '');
          const dayMode = p.invoiceBillingMode === 'day_rate_required';
          setInvoiceBillingMode(dayMode ? 'day_rate_required' : 'hourly');
          setQbVendorName(p.qbVendorName || '');
          setW9Status(isContractorW9OnFile(p) ? 'on_file' : parseContractorW9Status(p.w9Status));
          setDayRateFromAgreement(null);
          if (dayMode && !(p.defaultDayRate && p.defaultDayRate > 0)) {
            getContractorSignedAgreementDayRate(userId).then((rate) => {
              if (cancelled || rate == null) return;
              setDefaultDayRate(String(rate));
              setDayRateFromAgreement(rate);
            });
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const onInvoiceBillingModeChange = (next: ContractorInvoiceBillingMode) => {
    const switchingToDay = next === 'day_rate_required' && invoiceBillingMode !== 'day_rate_required';
    setInvoiceBillingMode(next);
    if (switchingToDay) {
      void applyAgreementDayRateIfEmpty(defaultDayRate);
    } else {
      setDayRateFromAgreement(null);
    }
  };

  const saveProfile = useCallback(async (): Promise<boolean> => {
    if (!canManage) return true;
    setSaving(true);
    setError(null);
    try {
      const hourly = parseFloat(defaultHourlyRate);
      let day = parseFloat(defaultDayRate);
      if (invoiceBillingMode === 'day_rate_required' && !(Number.isFinite(day) && day > 0)) {
        const fromAgreement = await applyAgreementDayRateIfEmpty(defaultDayRate);
        if (fromAgreement != null) day = fromAgreement;
      }
      await upsertContractorProfile(userId, {
        businessName: businessName.trim() || undefined,
        defaultHourlyRate: Number.isFinite(hourly) ? hourly : 0,
        defaultDayRate: Number.isFinite(day) && day > 0 ? day : undefined,
        invoiceBillingMode,
        qbVendorName: qbVendorName.trim() || undefined,
        w9Status,
        isActive: true,
      });
      const refreshed = await getContractorProfile(userId);
      setProfile(refreshed);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    applyAgreementDayRateIfEmpty,
    businessName,
    canManage,
    defaultDayRate,
    defaultHourlyRate,
    invoiceBillingMode,
    qbVendorName,
    userId,
    w9Status,
  ]);

  useImperativeHandle(ref, () => ({ saveIfNeeded: saveProfile }), [saveProfile]);

  const onW9File = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canManage) return;
    setUploading(true);
    setError(null);
    try {
      await uploadContractorW9(userId, file);
      setW9Status('on_file');
      const refreshed = await getContractorProfile(userId);
      setProfile(refreshed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'W-9 upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openW9 = async () => {
    if (!profile?.w9StoragePath) return;
    try {
      const url = await getContractorW9DownloadUrl(profile.w9StoragePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open W-9');
    }
  };

  if (loading) {
    return <p className="um-contractor-loading">Loading contractor profile…</p>;
  }

  if (!canManage) {
    return (
      <div className="um-contractor-readonly">
        <p>
          <strong>{displayName}</strong> is flagged as a 1099 contractor.
        </p>
        {profile && (
          <p className="um-contractor-meta">
            Billing:{' '}
            {profile.invoiceBillingMode === 'day_rate_required' ? 'Day rate (required)' : 'Hourly'} · W-9:{' '}
            {W9_LABELS[profile.w9Status]}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="um-contractor-panel">
      <h4 className="um-contractor-title">1099 Contractor profile</h4>
      <p className="um-contractor-hint">
        Configure pay rate and W-9 before {displayName} can submit timesheets. Saves with{' '}
        <strong>Save contractor profile</strong> or <strong>Save Changes</strong> on the user.
      </p>

      <div className="um-field">
        <label>Business name (optional)</label>
        <input
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="LLC or trade name"
        />
      </div>

      <div className="um-field">
        <label>Invoice billing</label>
        <select
          value={invoiceBillingMode}
          onChange={(e) => onInvoiceBillingModeChange(e.target.value as ContractorInvoiceBillingMode)}
        >
          <option value="hourly">Hourly (hours × rate)</option>
          <option value="day_rate_required">Day rate (required on invoices)</option>
        </select>
      </div>

      <div className="um-field-row">
        {invoiceBillingMode === 'day_rate_required' ? (
          <div className="um-field">
            <label>Default day rate</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={defaultDayRate}
              onChange={(e) => {
                setDefaultDayRate(e.target.value);
                setDayRateFromAgreement(null);
              }}
              placeholder="0.00"
            />
            {dayRateFromAgreement != null && (
              <p className="um-hours-help" style={{ margin: '6px 0 0' }}>
                Pre-filled from signed agreement (${dayRateFromAgreement}/day). You can adjust before saving.
              </p>
            )}
          </div>
        ) : (
          <div className="um-field">
            <label>Default hourly rate</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={defaultHourlyRate}
              onChange={(e) => setDefaultHourlyRate(e.target.value)}
              placeholder="0.00"
            />
          </div>
        )}
        <div className="um-field">
          <label>QuickBooks vendor hint</label>
          <input
            type="text"
            value={qbVendorName}
            onChange={(e) => setQbVendorName(e.target.value)}
            placeholder="Vendor name in QB"
          />
        </div>
      </div>

      <div className="um-field-row">
        <div className="um-field">
          <label>W-9 status</label>
          <select value={w9Status} onChange={(e) => setW9Status(e.target.value as ContractorW9Status)}>
            <option value="missing">Missing</option>
            <option value="on_file">On file</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        <div className="um-field um-contractor-w9-actions">
          <label>W-9 document</label>
          <div className="um-contractor-w9-btns">
            <input ref={fileRef} type="file" accept=".pdf,image/*" className="um-photo-input-hidden" onChange={onW9File} />
            <button type="button" className="um-btn-secondary" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <Upload size={14} />
              {uploading ? 'Uploading…' : 'Upload W-9'}
            </button>
            {profile?.w9StoragePath && (
              <button type="button" className="um-btn-secondary" onClick={openW9}>
                <FileText size={14} />
                View W-9
              </button>
            )}
          </div>
        </div>
      </div>

      {error && <p className="um-contractor-error">{error}</p>}

      <button type="button" className="um-btn-save um-contractor-save" onClick={() => void saveProfile()} disabled={saving}>
        {saving ? 'Saving…' : 'Save contractor profile'}
      </button>
    </div>
  );
},
);

export default ContractorProfileEditor;
